import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import {
  onAuthChange,
  signInWithEmail,
  signUpWithEmail,
  signInWithGoogle,
  signOut as firebaseSignOut,
  changePassword as firebaseChangePassword,
} from '@/services/firebase/auth';
import { getCurrentUser, createUser, repairUser } from '@/services/api/users';
import type { AuthState } from '@/types';

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, username: string) => Promise<void>;
  signInGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    firebaseUser: null,
    isAuthenticated: false,
    isAdmin: false,
    isLoading: true,
  });

  const fetchUser = useCallback(async (firebaseUser: FirebaseUser) => {
    try {
      const user = await getCurrentUser();
      setState({
        user,
        firebaseUser,
        isAuthenticated: true,
        isAdmin: user.isAdmin,
        isLoading: false,
      });
    } catch (error) {
      // User might not exist in our system yet, try to repair
      console.error('Error fetching user:', error);
      try {
        const user = await repairUser();
        setState({
          user,
          firebaseUser,
          isAuthenticated: true,
          isAdmin: user.isAdmin,
          isLoading: false,
        });
      } catch {
        // Still failed, clear state
        setState({
          user: null,
          firebaseUser,
          isAuthenticated: false,
          isAdmin: false,
          isLoading: false,
        });
      }
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthChange(async (firebaseUser) => {
      if (firebaseUser) {
        await fetchUser(firebaseUser);
      } else {
        setState({
          user: null,
          firebaseUser: null,
          isAuthenticated: false,
          isAdmin: false,
          isLoading: false,
        });
      }
    });

    return unsubscribe;
  }, [fetchUser]);

  const signIn = async (email: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      await signInWithEmail(email, password);
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const signUp = async (email: string, password: string, username: string) => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const { user: firebaseUser } = await signUpWithEmail(email, password);
      const user = await createUser(username);
      setState({
        user,
        firebaseUser,
        isAuthenticated: true,
        isAdmin: user.isAdmin,
        isLoading: false,
      });
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const signInGoogle = async () => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      await signInWithGoogle();
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const signOut = async () => {
    await firebaseSignOut();
    setState({
      user: null,
      firebaseUser: null,
      isAuthenticated: false,
      isAdmin: false,
      isLoading: false,
    });
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    await firebaseChangePassword(currentPassword, newPassword);
  };

  const refreshUser = async () => {
    if (state.firebaseUser) {
      await fetchUser(state.firebaseUser);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signIn,
        signUp,
        signInGoogle,
        signOut,
        changePassword,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
